import { useState, useEffect } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { ArrowLeftRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtD = (d) => { try { return format(parseISO(d), 'EEE d MMM'); } catch { return d; } };

export const AdminSwapDialog = ({ open, onOpenChange, sourceShift, category, onDone }) => {
  const [targetDate, setTargetDate] = useState('');
  const [targets, setTargets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [sendEmail, setSendEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setTargetDate(''); setTargets([]); setSelected(null); setSendEmail(false); }
  }, [open]);

  useEffect(() => {
    if (!targetDate || !open) return;
    setLoading(true);
    setSelected(null);
    axios.get(`${API}/shifts/calendar`, { params: { start_date: targetDate, end_date: targetDate } })
      .then((res) => {
        const rows = (res.data || []).filter((s) =>
          (s.shift_category || 'event') === category &&
          s.status === 'scheduled' &&
          s.employee_id !== sourceShift?.employee_id &&
          s.id !== sourceShift?.id);
        setTargets(rows);
      })
      .catch(() => toast.error('Could not load shifts for that date'))
      .finally(() => setLoading(false));
  }, [targetDate, open, category, sourceShift]);

  const doSwap = async () => {
    if (!selected) { toast.error('Pick the shift to swap with'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/admin/shifts/swap`, {
        shift_a_id: sourceShift.id,
        shift_b_id: selected.id,
        notify: sendEmail,
      });
      toast.success(`Swap completed${res.data.emails_sent ? ` — ${res.data.emails_sent} email(s) sent` : sendEmail ? '' : ' (no emails sent)'}`);
      onOpenChange(false);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Swap failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!sourceShift) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !submitting && onOpenChange(v)}>
      <DialogContent className="bg-card border-border rounded-sm max-w-lg" data-testid="admin-swap-dialog">
        <DialogHeader>
          <DialogTitle className="font-barlow tracking-wider uppercase flex items-center gap-2">
            <ArrowLeftRight className="w-5 h-5 text-primary" /> Swap Shift
          </DialogTitle>
          <DialogDescription>
            {sourceShift.employee_name} — {fmtD(sourceShift.date)}
            {sourceShift.start_time ? `, ${sourceShift.start_time} – ${sourceShift.end_time}` : ' (times TBC)'}
            {sourceShift.site_name ? ` @ ${sourceShift.site_name}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-muted-foreground">Swap with a shift on</label>
            <Input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)}
              className="h-9 rounded-sm mt-1" data-testid="swap-date-input" />
          </div>
          {loading && <p className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading shifts…</p>}
          {!loading && targetDate && targets.length === 0 && (
            <p className="text-xs text-muted-foreground italic">No swappable scheduled shifts on {fmtD(targetDate)}</p>
          )}
          {targets.length > 0 && (
            <div className="max-h-48 overflow-y-auto border border-border rounded-sm divide-y divide-border/50">
              {targets.map((t) => (
                <button key={t.id} onClick={() => setSelected(t)}
                  className={`w-full text-left px-3 py-2 text-xs flex items-center justify-between gap-2 hover:bg-secondary/50 ${selected?.id === t.id ? 'bg-primary/10 border-l-2 border-primary' : ''}`}
                  data-testid={`swap-target-${t.id}`}>
                  <span className="font-medium text-foreground">{t.employee_name}</span>
                  <span className="text-muted-foreground">{t.start_time ? `${t.start_time} – ${t.end_time}` : 'TBC'}{t.site_name ? ` · ${t.site_name}` : ''}</span>
                </button>
              ))}
            </div>
          )}
          {selected && (
            <div className="bg-secondary/40 border border-border rounded-sm p-3 text-xs space-y-1" data-testid="swap-preview">
              <p><strong className="text-primary">{sourceShift.employee_name}</strong> takes {fmtD(selected.date)}{selected.start_time ? `, ${selected.start_time} – ${selected.end_time}` : ' (TBC)'}{selected.site_name ? ` @ ${selected.site_name}` : ''}</p>
              <p><strong className="text-primary">{selected.employee_name}</strong> takes {fmtD(sourceShift.date)}{sourceShift.start_time ? `, ${sourceShift.start_time} – ${sourceShift.end_time}` : ' (TBC)'}{sourceShift.site_name ? ` @ ${sourceShift.site_name}` : ''}</p>
            </div>
          )}
          <label className="flex items-center gap-2 cursor-pointer">
            <Checkbox checked={sendEmail} onCheckedChange={(v) => setSendEmail(Boolean(v))} data-testid="swap-email-checkbox" />
            <span className="text-xs text-foreground">Send confirmation email to both employees</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button className="rounded-sm" onClick={doSwap} disabled={submitting || !selected} data-testid="swap-confirm-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <ArrowLeftRight className="w-4 h-4 mr-1.5" />}
            Confirm Swap
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminSwapDialog;
